import { useState, useEffect } from "react";
import { ChevronDown, Check, AlertCircle, Copy, ExternalLink, Wrench, Lock, Unlock, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { GeneratorActivationChecklist } from "./GeneratorActivationChecklist";
import type { LandingPageVariant } from "@/hooks/use-variant-assignment";

interface Generator {
  id: string;
  slug: string;
  display_name: string;
  is_active: boolean;
  is_production_locked: boolean;
  domains: string[];
  primary_domain: string | null;
}

interface DevModeChecklistProps {
  currentSlug: string;
  detectedHostname: string;
  isOverridden: boolean;
  currentVariantKey: string | null;
  availableVariants: LandingPageVariant[];
  onVariantChange: (key: string) => void;
}

export function DevModeChecklist({ currentSlug, detectedHostname, isOverridden, currentVariantKey, availableVariants, onVariantChange }: DevModeChecklistProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [generators, setGenerators] = useState<Generator[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchGenerators() {
      try {
        // Fetch generators with their domains
        const { data: generatorsData, error: genError } = await supabase
          .from("generators")
          .select("id, slug, display_name, is_active, is_production_locked")
          .order("display_name", { ascending: true });

        if (genError) throw genError;

        const { data: domainsData, error: domError } = await supabase
          .from("generator_domains")
          .select("generator_id, domain, is_primary");

        if (domError) throw domError;

        // Map domains to generators, and find primary domain
        const generatorsWithDomains = (generatorsData || []).map(gen => {
          const genDomains = (domainsData || []).filter(d => d.generator_id === gen.id);
          const primaryDomain = genDomains.find(d => d.is_primary)?.domain || null;
          return {
            ...gen,
            domains: genDomains.map(d => d.domain),
            primary_domain: primaryDomain,
          };
        });

        setGenerators(generatorsWithDomains);
      } catch (err) {
        console.error("Failed to fetch generators:", err);
      } finally {
        setIsLoading(false);
      }
    }

    if (isExpanded) {
      fetchGenerators();
    }
  }, [isExpanded]);

  const switchGenerator = (slug: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("generator", slug);
    window.location.href = url.toString();
  };

  const copyCurrentUrl = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("URL copied to clipboard");
  };

  const currentGenerator = generators.find(g => g.slug === currentSlug);

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm">
      <div className="bg-card border border-border rounded-lg shadow-lg overflow-hidden">
        {/* Header - Always visible */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between px-3 py-2 hover:bg-muted/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Wrench className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium text-sm">Dev Mode</span>
            {isOverridden && (
              <Badge variant="secondary" className="text-xs py-0 h-5">
                Override
              </Badge>
            )}
          </div>
          {isExpanded && (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          )}
        </button>

        {/* Expanded content */}
        {isExpanded && (
          <div className="border-t border-border px-3 py-2.5 space-y-3 text-xs">
            {/* Current Status */}
            <TooltipProvider delayDuration={200}>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground flex-shrink-0">Domain</span>
                  <code className="text-[10px] bg-muted px-1.5 py-0.5 rounded truncate" title={detectedHostname}>
                    {detectedHostname}
                  </code>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground flex-shrink-0">Generator</span>
                  <div className="flex items-center gap-1.5">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {currentGenerator?.is_production_locked ? (
                          <Lock className="w-3 h-3 text-amber-500 cursor-help" />
                        ) : (
                          <Unlock className="w-3 h-3 text-emerald-500 cursor-help" />
                        )}
                      </TooltipTrigger>
                      <TooltipContent side="left" className="text-xs">
                        {currentGenerator?.is_production_locked 
                          ? "Production locked — prompts cannot be edited" 
                          : "Unlocked — prompts can be edited"}
                      </TooltipContent>
                    </Tooltip>
                    <Badge variant={currentGenerator?.is_active ? "default" : "secondary"} className="text-[10px] h-5">
                      {currentSlug.toUpperCase()}
                    </Badge>
                  </div>
                </div>
                {isOverridden && (
                  <div className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400">
                    <AlertCircle className="w-3 h-3" />
                    <span>URL override active</span>
                  </div>
                )}
              </div>
            </TooltipProvider>

            {/* Generator Switcher */}
            <div className="space-y-1 border-t border-border pt-2.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] text-muted-foreground">Switch generator</span>
                {!isLoading && (
                  <span className="text-[9px] text-muted-foreground">
                    {generators.filter(g => g.is_active).length}/{generators.length} active
                  </span>
                )}
              </div>
              {isLoading ? (
                <div className="text-muted-foreground py-1">Loading...</div>
              ) : (
                <div className="max-h-[300px] overflow-y-auto space-y-0.5 pr-2">
                  {generators.map(gen => (
                    <button
                      key={gen.id}
                      onClick={() => switchGenerator(gen.slug)}
                      disabled={gen.slug === currentSlug}
                      className={`w-full text-left px-2 py-1.5 rounded transition-colors ${
                        gen.slug === currentSlug
                          ? "bg-primary/10 text-primary cursor-default"
                          : "hover:bg-muted/50"
                      }`}
                    >
                      {/* Line 1: Name + lock + slug */}
                      <div className="flex items-center justify-between gap-1.5">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {gen.slug === currentSlug && (
                            <Check className="w-3 h-3 flex-shrink-0" />
                          )}
                          <span className={`truncate ${gen.slug === currentSlug ? "font-medium" : ""}`}>
                            {gen.display_name}
                          </span>
                          {gen.is_production_locked && (
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Lock className="w-2.5 h-2.5 text-amber-500 cursor-help flex-shrink-0" />
                                </TooltipTrigger>
                                <TooltipContent side="left" className="text-xs">
                                  Production locked
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        <code className="text-[10px] text-muted-foreground flex-shrink-0">
                          {gen.slug}
                        </code>
                      </div>
                      {/* Line 2: Domain + status */}
                      <div className="flex items-center justify-between mt-0.5 pl-[18px]">
                        <span className="text-[10px] text-muted-foreground truncate">
                          {gen.primary_domain || "—"}
                        </span>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className={`w-1.5 h-1.5 rounded-full ${gen.is_active ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
                          <span className={`text-[10px] ${gen.is_active ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                            {gen.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Configured Domains */}
            {!isLoading && currentGenerator && currentGenerator.domains.length > 0 && (
              <div className="space-y-1 border-t border-border pt-2.5">
                <div className="text-[10px] text-muted-foreground mb-1">Configured domains</div>
                <div className="text-[11px] space-y-0.5">
                  {currentGenerator.domains.map(domain => (
                    <div key={domain} className="flex items-center gap-1.5 text-muted-foreground px-1">
                      <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
                      <span className="truncate">{domain}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Activation Checklist */}
            {currentGenerator && (
              <div className="border-t border-border pt-2.5">
                <GeneratorActivationChecklist 
                  generatorId={currentGenerator.id} 
                  generatorSlug={currentGenerator.slug}
                />
              </div>
            )}

            {/* A/B Variant Toggle */}
            {availableVariants.length > 1 && (
              <div className="space-y-1 border-t border-border pt-2.5">
                <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-1.5">
                  <FlaskConical className="w-3 h-3" />
                  <span>A/B Variant</span>
                  <Badge variant="secondary" className="text-[9px] h-4 px-1">
                    {currentVariantKey || "—"}
                  </Badge>
                </div>
                <div className="flex gap-1">
                  {availableVariants.map(v => (
                    <button
                      key={v.variant_key}
                      onClick={() => onVariantChange(v.variant_key)}
                      className={`flex-1 text-center px-2 py-1.5 text-xs rounded transition-colors ${
                        v.variant_key === currentVariantKey
                          ? "bg-primary/10 text-primary font-medium"
                          : "hover:bg-muted/50 text-muted-foreground"
                      }`}
                    >
                      <div className="font-medium">{v.variant_key}</div>
                      <div className="text-[9px] text-muted-foreground">{v.layout_name}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="pt-2 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                onClick={copyCurrentUrl}
                className="w-full gap-1.5 h-7 text-xs text-muted-foreground hover:text-foreground"
              >
                <Copy className="w-3 h-3" />
                Copy URL
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
